import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { MaterialV1, VideoV1 } from '@ilteducation/content-registry-types';
import { getTitle } from '@/foundations/helpers/videoUtils';
import customProvider, { Params } from '@/data-provider/customProvider';
import eventBus from '@/foundations/services/eventBus';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';

type Props = {
  item: VideoV1;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'MaterialLinks',
  props: {
    item: null
  },
  setup(__props: any) {

const props = __props as {
  item: VideoV1;
}



const groupKey = (material: MaterialV1) => material.originalTitle || material.title;

const selectedMaterials = ref<MaterialV1[]>([]);
const selectedIds = ref<string[]>([]);
const initialIds = ref<string[]>([]);
const changed = computed(() => {
  if (selectedIds.value.length !== initialIds.value.length) {
    return true;
  }
  return selectedIds.value.some((id) => !initialIds.value.includes(id));
});

const selectedFilteredIds = ref<string[]>([]);
const foundMaterials = ref<MaterialV1[]>([]);
const filteredMaterials = computed(() => {
  const foundAndSelected = [...foundMaterials.value, ...selectedMaterials.value];
  return foundAndSelected.filter(
    (material, index) => foundAndSelected.findIndex((m) => groupKey(m) === groupKey(material)) === index,
  );
});

const loadMaterials = (filter: Params['filter']): Promise<MaterialV1[]> => {
  const params: Params = {
    filter: filter,
    sort: [],
    pagination: {
      perPage: 15,
      page: 1,
    },
    include: undefined,
  };
  return customProvider()
    .getList('materials', params)
    .then((response) => {
      return response.data as MaterialV1[];
    });
};

watch(
  () => props.item,
  () => {
    loadMaterials({
      'contentLinks.id': props.item.id,
    }).then((response) => {
      selectedMaterials.value = response;
      selectedIds.value = selectedMaterials.value.map((material) => material.id);

      selectedFilteredIds.value = selectedMaterials.value
        .filter(
          (material, index) => selectedMaterials.value.findIndex((m) => groupKey(m) === groupKey(material)) === index,
        )
        .map((material) => material.id);

      initialIds.value = selectedIds.value;
    });
  },
  { immediate: true },
);

const search = (value) => {
  if (value && value.length > 2) {
    loadMaterials({
      q: value,
    }).then((response) => {
      foundMaterials.value = response as MaterialV1[];
    });
  }
};
const selected = () => {
  const foundAndSelected = [...foundMaterials.value, ...selectedMaterials.value];
  const keys = foundAndSelected.filter((material) => selectedFilteredIds.value.includes(material.id)).map(groupKey);
  selectedIds.value = foundAndSelected
    .filter((material) => keys.includes(groupKey(material)))
    .map((material) => material.id);
};

const materialHeaders = [
  { text: 'Title', value: 'title' },
  { text: 'Language', value: 'language' },
  { text: 'Market', value: 'realm' },
  { text: 'Subscriptions', value: 'accessRules.subscriptionType' },
];

const subscriptionLabel = (value: string) =>
  defaultSubscriptions.find((subscription) => subscription.value === value)?.subscription ?? value;

const getEditLink = (material: MaterialV1) => ({ name: 'materials_edit', params: { id: material.id } });

const save = async () => {
  const addedIds = selectedIds.value.filter((id) => !initialIds.value.includes(id));
  const removedIds = initialIds.value.filter((id) => !selectedIds.value.includes(id));
  const additions = addedIds.map(async (id) => {
    const addLink = customProvider().update('materials', {
      id,
      data: { 'add_/contentLinks/_association': { id: props.item.id, contentType: 'Video' } },
    });
    return addLink.catch(async (reason) => {
      // @todo: fix content-registry to accept adding to optional array property via json patch
      if (reason.response?.status !== 500) {
        return Promise.reject(reason);
      }
      return customProvider().update('materials', {
        id,
        data: { 'add_/contentLinks_association': [{ id: props.item.id, contentType: 'Video' }] },
      });
    });
  });
  const removals = removedIds.map((id) => {
    // json patch accepts only the index of the item to be removed
    // find the index of the content link in the material
    const material = selectedMaterials.value.find((material) => material.id === id);
    if (!material) {
      return Promise.resolve();
    }
    const index = material.contentLinks?.findIndex((link) => link.id === props.item.id && link.contentType === 'Video');
    customProvider().update('materials', {
      id,
      // value is not important when removing by index but required for custom provider
      data: { [`remove_/contentLinks/${index}_association`]: { id: props.item.id, contentType: 'Video' } },
    });
  });
  return await Promise.all([...additions, ...removals]).then(() => {
    initialIds.value = selectedIds.value;
    eventBus.$emit('snackbarV2.open', { text: 'Materials saved successfully.', color: 'success' });
  });
};

return { __sfc: true,props, groupKey, selectedMaterials, selectedIds, initialIds, changed, selectedFilteredIds, foundMaterials, filteredMaterials, loadMaterials, search, selected, materialHeaders, subscriptionLabel, getEditLink, save, getTitle }
}

})